import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaEye, FaChevronLeft, FaChevronRight, FaGraduationCap, FaExpandArrowsAlt, FaTrash } from 'react-icons/fa';
import PremiumActivityModalShell from './PremiumActivityModalShell';
import { ReimaginedArtworkExpandedView } from './ReimaginedArtworkDetailsModal';
import { coLearningInkBody, coLearningInkMuted, coLearningInkTitle } from '../ui/coLearningModalTheme';
import { getTransformedArtworksForOriginal, ACTIVITY_TYPES } from '../utils/activityService';
import { auth } from '../firebase';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { db } from '../firebase';
import { deleteDoc, doc } from 'firebase/firestore';
import { API_ENDPOINTS } from '../config/backendConfig';

const WaysOfSeeingModal = ({ isOpen, onClose, originalArtwork, originalArtworkTitle }) => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const handleSignOut = async () => {
    try { await logout(); } catch (_) {}
    try { localStorage.removeItem('mentor_session'); window.dispatchEvent(new Event('mentor_session_changed')); } catch (_) {}
    navigate('/');
  };
  const [transformedArtworks, setTransformedArtworks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [imageExpanded, setImageExpanded] = useState(false);
  const [expandedSrc, setExpandedSrc] = useState('');
  const [resolvedOriginal, setResolvedOriginal] = useState('');

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.includes('bfl.ai') || url.includes('delivery-eu4.bfl.ai')) {
      return `${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(url)}`;
    }
    if (url.startsWith('/')) {
      return `${API_ENDPOINTS.backendBase}${url}`;
    }
    return url;
  };

  const getCurrentTransformed = () => {
    const art = transformedArtworks[currentIndex];
    if (!art) {
      console.warn('⚠️ getCurrentTransformed: No artwork at index', currentIndex);
      return '';
    }
    // Prefer resolved URL (already processed), then transform others
    let url = art._resolvedUrl || art.transformedImageUrl || art.imageUrl || '';
    
    // If _resolvedUrl exists, it's already been processed, so use it directly
    // Otherwise, process the URL through getImageUrl
    if (art._resolvedUrl) {
      console.log('🖼️ getCurrentTransformed: Using pre-resolved URL:', {
        index: currentIndex,
        artworkId: art.id,
        url: url.substring(0, 100)
      });
      return url;
    }
    
    // Process URL if not already resolved
    const finalUrl = getImageUrl(url);
    console.log('🖼️ getCurrentTransformed: Processed URL:', {
      index: currentIndex,
      artworkId: art.id,
      originalUrl: url.substring(0, 100),
      finalUrl: finalUrl.substring(0, 100),
      hasResolvedUrl: !!art._resolvedUrl,
      hasTransformedImageUrl: !!art.transformedImageUrl,
      hasImageUrl: !!art.imageUrl
    });
    return finalUrl;
  };

  const showConfirm = (options) => {
    const { title = 'Delete this upload?', message = 'This action cannot be undone.' } = options || {};
    return new Promise((resolve) => {
      const container = document.createElement('div');
      container.className =
        'fixed inset-0 z-[200200] flex items-center justify-center p-4';
      container.innerHTML = `
        <div class=\"absolute inset-0 bg-[#2f251b]/35 backdrop-blur-sm\"></div>
        <div class=\"relative w-full max-w-md rounded-[22px] border border-[#e8d4bc]/90 bg-gradient-to-b from-[#fffdf9] to-[#fff6ec] p-6 shadow-[0_24px_60px_rgba(60,40,20,0.18)] ring-1 ring-[#efe2d0]/80\">
          <h3 class=\"mb-2 text-xl font-semibold text-[#2F251B]\">${title}</h3>
          <p class=\"mb-6 text-sm leading-relaxed text-[#5A4632]\">${message}</p>
          <div class=\"flex justify-end gap-3\">
            <button id=\"am-cancel\" class=\"rounded-xl border border-[#e8d4bc] bg-white px-4 py-2.5 text-sm font-medium text-[#5A4632] shadow-sm transition hover:bg-[#fffdf9]\">Cancel</button>
            <button id=\"am-ok\" class=\"rounded-xl bg-[#2F251B] px-4 py-2.5 text-sm font-semibold text-[#fffdf9] shadow-md transition hover:bg-[#3d3228]\">Delete</button>
          </div>
        </div>`;
      document.body.appendChild(container);
      const cleanup = () => document.body.removeChild(container);
      container.querySelector('#am-cancel')?.addEventListener('click', () => { cleanup(); resolve(false); });
      container.querySelector('#am-ok')?.addEventListener('click', () => { cleanup(); resolve(true); });
    });
  };

  const handleDeleteCurrent = async () => {
    const current = transformedArtworks[currentIndex];
    if (!current?.id) return;
    
    // Only allow deleting tutorial uploads (not reimagine/style transformations)
    if (current?.type !== 'tutorial') {
      await showConfirm({
        title: 'Deletion not available here',
        message: 'You can only delete tutorial uploads from this window. To remove a transformation/journey, please use the Activity Dashboard.'
      });
      return;
    }
    
    const confirmed = await showConfirm({ 
      title: 'Delete this upload?', 
      message: 'This will remove it from your Ways of Seeing.' 
    });
    if (!confirmed) return;
    
    try {
      console.log('🗑️ Deleting tutorial artwork:', {
        id: current.id,
        type: current.type,
        title: current.title
      });
      
      // Delete from the correct collection based on the data source
      if (current.tutorialData?.tutorialArtworkId) {
        // This is from tutorialArtworks collection
        await deleteDoc(doc(db, 'tutorialArtworks', current.id));
        console.log('✅ Deleted from tutorialArtworks collection');
      } else {
        // This is from userActivities collection
        await deleteDoc(doc(db, 'userActivities', current.id));
        console.log('✅ Deleted from userActivities collection');
      }
      
      // Update the local state
      setTransformedArtworks(prev => prev.filter(a => a.id !== current.id));
      
      // Adjust the current index if needed
      setCurrentIndex(prev => {
        const newLength = transformedArtworks.length - 1;
        if (newLength === 0) return 0;
        return Math.max(0, Math.min(prev, newLength - 1));
      });
      
      console.log('✅ Tutorial artwork deleted successfully');
      
    } catch (e) {
      console.error('❌ Failed to delete tutorial artwork:', e);
      alert('Failed to delete the tutorial artwork. Please try again.');
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (isOpen && originalArtwork && user) {
      fetchTransformedArtworks();
    }
  }, [isOpen, originalArtwork, user]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (imageExpanded) setImageExpanded(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, imageExpanded, onClose]);

  useEffect(() => {
    const resolve = async () => {
      if (!originalArtwork) { setResolvedOriginal(''); return; }
      try {
        // Firebase Storage URL (gs://...)
        if (originalArtwork.startsWith('gs://')) {
          const storage = getStorage();
          const storageRef = ref(storage, originalArtwork);
          const url = await getDownloadURL(storageRef);
          setResolvedOriginal(url);
          return;
        }
        // BFL proxied image
        if (originalArtwork.includes('bfl.ai') || originalArtwork.includes('delivery-eu4.bfl.ai')) {
          setResolvedOriginal(`${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(originalArtwork)}`);
          return;
        }
        // Relative path served by backend
        if (originalArtwork.startsWith('/')) {
          setResolvedOriginal(`${API_ENDPOINTS.backendBase}${originalArtwork}`);
          return;
        }
        // Otherwise assume absolute http(s)
        setResolvedOriginal(originalArtwork);
      } catch (e) {
        console.warn('Failed to resolve original artwork URL:', e?.message);
        setResolvedOriginal(originalArtwork);
      }
    };
    resolve();
  }, [originalArtwork]);

  const fetchTransformedArtworks = async () => {
    if (!originalArtwork || !user) return;
    
    setLoading(true);
    try {
      console.log('🔍 WaysOfSeeingModal: Fetching transformed artworks for:', {
        originalArtwork: originalArtwork.substring(0, 100) + '...',
        userId: user.uid
      });
      
      const artworks = await getTransformedArtworksForOriginal(originalArtwork, user.uid);
      
      console.log('🔍 WaysOfSeeingModal: Raw artworks received:', {
        total: artworks.length,
        types: artworks.map(a => ({ id: a.id, type: a.type, style: a.styleInfo?.name }))
      });
      
      // Only include tutorial uploads (NOT style transfers/reimagine activities)
      // Filter out reimagine activities (type === 'reimagine' or feature includes 'style-transfer')
      // Only show tutorial artworks (type === 'tutorial' or from tutorialArtworks collection)
      const tutorialOnly = (artworks || [])
        .filter(a => {
          // Exclude reimagine/style transfer activities
          if (a.type === 'reimagine' || a.feature === 'reimagine' || a.feature?.includes('style-transfer')) {
            return false;
          }
          // Only include tutorial artworks
          return a.type === 'tutorial' || a.type === ACTIVITY_TYPES.TUTORIAL || !a.type;
        })
        .filter(a => !!(a?.transformedImageUrl || a?.imageUrl))
        .sort((a,b) => {
          const ta = new Date(a.createdAt || a.timestamp || 0).getTime();
          const tb = new Date(b.createdAt || b.timestamp || 0).getTime();
          return tb - ta; // newest first
        });
      
      // Deduplicate by image URL to prevent showing the same artwork twice
      const seenUrls = new Set();
      const allUploads = tutorialOnly.filter(a => {
        const url = a.transformedImageUrl || a.imageUrl || '';
        if (!url) return false;
        if (seenUrls.has(url)) {
          console.log('🔍 Filtering duplicate artwork by URL:', {
            id: a.id,
            url: url.substring(0, 80)
          });
          return false;
        }
        seenUrls.add(url);
        return true;
      });

      console.log('🔍 WaysOfSeeingModal: Aggregated uploads:', {
        count: allUploads.length,
        types: allUploads.map(u => ({ id: u.id, type: u.type, style: u.styleInfo?.name }))
      });

      console.log('🔍 WaysOfSeeingModal: Before URL resolution, allUploads:', {
        count: allUploads.length,
        artworks: allUploads.map(a => ({
          id: a.id,
          type: a.type,
          style: a.styleInfo?.name || a.styleName,
          hasTransformedImageUrl: !!a.transformedImageUrl,
          hasImageUrl: !!a.imageUrl,
          transformedImageUrl: a.transformedImageUrl?.substring(0, 60),
          imageUrl: a.imageUrl?.substring(0, 60)
        }))
      });

      // Resolve any gs:// and normalize URLs for all uploads
      const storage = getStorage();
      const normalized = await Promise.all(
        allUploads.map(async (a) => {
          // Try all possible URL fields in order of preference
          let u = a.transformedImageUrl || a.imageUrl || a._resolvedUrl || '';
          
          console.log('🖼️ WaysOfSeeingModal: Resolving image URL for artwork:', {
            id: a.id,
            type: a.type,
            style: a.styleInfo?.name || a.styleName,
            originalUrl: u ? u.substring(0, 100) : 'EMPTY',
            hasTransformedImageUrl: !!a.transformedImageUrl,
            hasImageUrl: !!a.imageUrl,
            hasResolvedUrl: !!a._resolvedUrl,
            fullData: {
              transformedImageUrl: a.transformedImageUrl?.substring(0, 50),
              imageUrl: a.imageUrl?.substring(0, 50)
            }
          });
          
          // If already a data URL or blob URL, use as-is
          if (u.startsWith('data:') || u.startsWith('blob:')) {
            console.log('✅ Using data/blob URL as-is');
            return { ...a, _resolvedUrl: u };
          }
          
          try {
            if (u.startsWith('gs://')) {
              console.log('📦 Resolving Firebase Storage URL:', u);
              const storageRef = ref(storage, u);
              u = await getDownloadURL(storageRef);
              console.log('✅ Resolved Firebase Storage to:', u.substring(0, 100));
            } else if (u.includes('bfl.ai') || u.includes('delivery-eu4.bfl.ai')) {
              u = `${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(u)}`;
              console.log('✅ Proxied BFL URL');
            } else if (u.startsWith('/')) {
              // Relative path - prepend backend base URL
              u = `${API_ENDPOINTS.backendBase}${u}`;
              console.log('✅ Resolved relative path to:', u.substring(0, 100));
            } else if (u.startsWith('http://') || u.startsWith('https://')) {
              console.log('✅ Using absolute HTTP(S) URL as-is');
            } else if (u) {
              // Try to treat as relative path if it doesn't start with /
              if (!u.startsWith('/')) {
                u = `${API_ENDPOINTS.backendBase}/${u}`;
                console.log('✅ Treated as relative path, resolved to:', u.substring(0, 100));
              } else {
                console.log('✅ Using URL as-is (unknown format)');
              }
            } else {
              console.warn('⚠️ No image URL found for artwork:', {
                id: a.id,
                type: a.type,
                availableFields: Object.keys(a).filter(k => k.includes('url') || k.includes('image') || k.includes('Image'))
              });
            }
          } catch (err) {
            console.error('❌ Failed to resolve image URL:', {
              artworkId: a.id,
              artworkType: a.type,
              originalUrl: u ? u.substring(0, 100) : 'EMPTY',
              error: err.message,
              stack: err.stack
            });
            // Keep the original URL even if resolution failed
          }
          return { ...a, _resolvedUrl: u || '' };
        })
      );

      console.log('✅ WaysOfSeeingModal: Normalized artworks with resolved URLs:', {
        count: normalized.length,
        resolved: normalized.map(a => ({
          id: a.id,
          style: a.styleInfo?.name || a.styleName,
          type: a.type,
          hasResolvedUrl: !!a._resolvedUrl,
          resolvedUrlPreview: a._resolvedUrl?.substring(0, 100),
          hasTransformedImageUrl: !!a.transformedImageUrl,
          hasImageUrl: !!a.imageUrl
        }))
      });

      // Filter out any artworks that don't have a valid image URL after resolution
      const validArtworks = normalized.filter(a => {
        const hasUrl = !!(a._resolvedUrl || a.transformedImageUrl || a.imageUrl);
        if (!hasUrl) {
          console.warn('⚠️ Filtering out artwork with no image URL:', {
            id: a.id,
            style: a.styleInfo?.name || a.styleName
          });
        }
        return hasUrl;
      });

      console.log('✅ WaysOfSeeingModal: Final valid artworks:', {
        count: validArtworks.length,
        artworks: validArtworks.map(a => ({
          id: a.id,
          style: a.styleInfo?.name || a.styleName,
          hasUrl: !!(a._resolvedUrl || a.transformedImageUrl || a.imageUrl)
        }))
      });

      // Sort by max reactions first (kudos total), then by latest contribution (createdAt desc).
      // This keeps the browsing order consistent across co-learning spaces.
      try {
        const ids = validArtworks.map(a => a.id).filter(Boolean);
        const totals = await Promise.all(
          ids.map(async (id) => {
            try {
              const resp = await fetch(`${API_ENDPOINTS.getKudosCount}?artworkId=${id}`);
              const data = await resp.json();
              if (data?.success) return [id, data?.totalReactions ?? 0];
              return [id, 0];
            } catch (_) {
              return [id, 0];
            }
          })
        );
        const reactionMap = {};
        totals.forEach(([id, total]) => { reactionMap[id] = total; });

        const toMillis = (t) => {
          try {
            if (!t) return 0;
            if (typeof t?.toMillis === 'function') return t.toMillis();
            if (t?._seconds) return t._seconds * 1000 + (t._nanoseconds || 0) / 1000000;
            if (t instanceof Date) return t.getTime();
            const d = new Date(t);
            return isNaN(d.getTime()) ? 0 : d.getTime();
          } catch (_) { return 0; }
        };

        validArtworks.sort((a, b) => {
          const ra = reactionMap[a?.id] ?? 0;
          const rb = reactionMap[b?.id] ?? 0;
          if (rb !== ra) return rb - ra;
          const ta = toMillis(a?.createdAt || a?.timestamp);
          const tb = toMillis(b?.createdAt || b?.timestamp);
          return tb - ta;
        });
      } catch (e) {
        // Keep createdAt ordering on failure
      }

      setTransformedArtworks(validArtworks);
      setCurrentIndex(0);
    } catch (error) {
      console.error('❌ WaysOfSeeingModal: Error fetching transformed artworks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    setCurrentIndex(prev => prev > 0 ? prev - 1 : transformedArtworks.length - 1);
  };

  const handleNext = () => {
    setCurrentIndex(prev => prev < transformedArtworks.length - 1 ? prev + 1 : 0);
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'reimagine':
        return <FaEye className="text-xl text-[#7c6a93]" />;
      case 'tutorial':
        return <FaGraduationCap className="text-xl text-[#6b7c93]" />;
      default:
        return <FaEye className="text-xl text-[#8A7357]" />;
    }
  };

  const getActivityTypeLabel = (type) => {
    switch (type) {
      case 'reimagine':
        return 'Reimagined';
      case 'tutorial':
        return 'Tutorial';
      default:
        return 'Transformed';
    }
  };

  if (!isOpen || typeof document === 'undefined') return null;

  const modalTree = (
    <>
      <PremiumActivityModalShell
        open={isOpen}
        onClose={onClose}
        title="Ways of Seeing"
        eyebrow="Magic Studio"
        subtitle={originalArtworkTitle || 'Original artwork'}
        maxWidthClass="max-w-[min(96vw,1152px)]"
        zIndexClass="z-[200120]"
        ariaLabelledBy="ways-of-seeing-modal-title"
      >
        <div className="bg-[#FFFDF9]/98 p-4 sm:p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 sm:py-20">
              <div className="h-12 w-12 animate-spin rounded-full border-2 border-[#efe2d0] border-t-[#8A7357]" />
              <span className="text-sm font-medium text-[#5A4632] sm:text-base">Loading transformations…</span>
            </div>
          ) : transformedArtworks.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#F5D99B] bg-[#FFF9EF]/80 px-6 py-12 text-center">
              <h3 className={`mb-2 text-xl font-semibold ${coLearningInkTitle}`}>No transformations yet</h3>
              <p className={`mb-6 text-sm leading-relaxed sm:text-base ${coLearningInkBody}`}>
                Open the style explorer to reimagine this piece — your results will show up here.
              </p>
              <button
                type="button"
                onClick={() => navigate('/art-world/selection', { state: { artwork: originalArtwork, title: originalArtworkTitle } })}
                className="inline-flex items-center justify-center rounded-full border border-[#E8C97A] bg-gradient-to-r from-amber-500 to-orange-400 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:from-amber-600 hover:to-orange-500"
              >
                Explore new art styles
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Learn New Art Styles CTA */}
              <div className="mb-1 flex justify-end lg:col-span-2">
                <button
                  type="button"
                  onClick={() => navigate('/art-world/selection', { state: { artwork: originalArtwork, title: originalArtworkTitle } })}
                  className="inline-flex items-center justify-center rounded-xl border border-[#e8d4bc] bg-gradient-to-b from-white to-[#f6f0e8] px-4 py-2 text-xs font-semibold text-[#2F251B] shadow-sm ring-1 ring-[#efe2d0]/70 transition hover:border-[#d4b896] hover:shadow-md sm:text-sm"
                >
                  Explore new art styles
                </button>
              </div>
              {/* Left: Original Artwork */}
              <div className="rounded-2xl border border-[#F5D99B]/60 bg-gradient-to-br from-[#FFFDF9] to-[#FFF7ED] p-4 shadow-[0_8px_24px_rgba(120,72,20,0.08)] sm:p-5">
                <h3 className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9A7A55]">Original</h3>
                <div className="relative h-[min(52vh,480px)] w-full overflow-hidden rounded-2xl border border-[#F5D99B]/50 bg-[#FFF9EF] sm:h-[480px]">
                  <img
                    src={resolvedOriginal}
                    alt="Original artwork"
                    className="h-full w-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedSrc(resolvedOriginal);
                      setImageExpanded(true);
                    }}
                    className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full border border-[#e8d4bc]/90 bg-white/95 text-[#5A4632] shadow-md transition hover:bg-white"
                    title="Expand"
                  >
                    <FaExpandArrowsAlt />
                  </button>
                </div>
              </div>

              {/* Right: Combined uploads scroller (reimagine + tutorial) */}
              <div className="rounded-2xl border border-[#F5D99B]/60 bg-gradient-to-br from-[#FFFDF9] to-[#FFF7ED] p-4 shadow-[0_8px_24px_rgba(120,72,20,0.08)] sm:p-5">
                <h3 className="mb-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9A7A55]">
                  Your Ways of Seeing ({transformedArtworks.length})
                </h3>
                <div className="relative">
                  {transformedArtworks.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={handlePrevious}
                        className="absolute left-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-full border border-[#e8d4bc]/90 bg-white/95 p-2 text-[#5A4632] shadow-md transition hover:bg-white"
                      >
                        <FaChevronLeft />
                      </button>
                      <button
                        type="button"
                        onClick={handleNext}
                        className="absolute right-2 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded-full border border-[#e8d4bc]/90 bg-white/95 p-2 text-[#5A4632] shadow-md transition hover:bg-white"
                      >
                        <FaChevronRight />
                      </button>
                    </>
                  )}
                  <div className="flex justify-center">
                    <div className="relative h-[min(52vh,480px)] w-full max-w-[720px] overflow-hidden rounded-2xl border border-[#e8d4bc]/80 bg-[#f4efe6] shadow-sm sm:h-[480px]">
                      {getCurrentTransformed() ? (
                        <img
                          src={getCurrentTransformed()}
                          alt={`Upload ${currentIndex + 1}`}
                          className="h-full w-full object-contain"
                          onError={(e) => {
                            console.error('❌ Main image failed to load:', getCurrentTransformed());
                            e.currentTarget.src = '';
                            e.currentTarget.alt = 'Image failed to load';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#f4efe6]">
                          <div className="text-center text-[#8A7357]">
                            <p className="text-lg font-semibold mb-2 text-[#5A4632]">Image not available</p>
                            <p className="text-sm text-[#8A7357]">The image URL may be invalid or inaccessible</p>
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedSrc(getCurrentTransformed() || '');
                          setImageExpanded(true);
                        }}
                        className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full border border-[#e8d4bc]/90 bg-white/95 text-[#5A4632] shadow-md transition hover:bg-white"
                        title="Expand"
                        disabled={!getCurrentTransformed()}
                      >
                        <FaExpandArrowsAlt />
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteCurrent}
                        className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-full border border-red-200/90 bg-white/95 text-red-600 shadow-md transition hover:bg-white"
                        title="Delete this upload"
                      >
                        <FaTrash />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#2F251B]/75 to-transparent p-3">
                        <div className="text-sm font-semibold text-[#fffdf9]">
                          {getActivityTypeLabel(transformedArtworks[currentIndex]?.type)} • {transformedArtworks[currentIndex]?.styleInfo?.name || 'Artwork'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reactions removed - Ways of Seeing is private to the user */}

                  {transformedArtworks.length > 1 && (
                    <div className="flex justify-start gap-2 mt-4 overflow-x-auto px-2 pb-1">
                      {transformedArtworks.map((art, idx) => {
                        // Use resolved URL if available, otherwise process it
                        let thumbUrl = art._resolvedUrl || '';
                        if (!thumbUrl) {
                          const rawUrl = art.transformedImageUrl || art.imageUrl || '';
                          thumbUrl = getImageUrl(rawUrl);
                        }
                        return (
                          <button
                            key={art.id}
                            type="button"
                            onClick={() => setCurrentIndex(idx)}
                            className={`h-16 w-20 overflow-hidden rounded-xl border-2 transition-all ${
                              idx === currentIndex
                                ? 'scale-105 border-[#8A7357] shadow-md ring-2 ring-[#efe2d0]'
                                : 'border-[#e8d4bc]/80 hover:border-[#d4b896]'
                            }`}
                          >
                            {thumbUrl ? (
                              <img 
                                src={thumbUrl} 
                                alt={`Thumb ${idx+1}`} 
                                className="w-full h-full object-contain bg-white rounded-lg"
                                onError={(e) => {
                                  console.error('❌ Thumbnail image failed to load:', {
                                    artworkId: art.id,
                                    url: thumbUrl.substring(0, 100),
                                    hasResolvedUrl: !!art._resolvedUrl,
                                    hasTransformedImageUrl: !!art.transformedImageUrl,
                                    hasImageUrl: !!art.imageUrl
                                  });
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center rounded-lg bg-[#f4efe6] text-xs text-[#8A7357]">
                                No image
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </PremiumActivityModalShell>
      <ReimaginedArtworkExpandedView
        open={imageExpanded}
        imageUrl={expandedSrc}
        onClose={() => setImageExpanded(false)}
      />
    </>
  );

  return createPortal(modalTree, document.body);
};

export default WaysOfSeeingModal;
